import type { AugDimWarmupPair } from '@tunasong/music-lib'
import { chordToEvents } from '../player/chord-to-events.js'
import { type Stab, useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Ear Training Aug/Dim Warm-up audition: two sequential block-chord stabs,
 * each ~1200 ms (half-note), separated by a 300 ms gap. The targetPosition
 * field determines whether the reference or target plays first.
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md §5.3
 */
const CHORD_MS = 1200
const GAP_MS = 300

export const buildAugDimWarmupStabs = (pair: AugDimWarmupPair): Stab[] => {
  const referenceEvents = chordToEvents(pair.referenceChord, '2n', pair.rootOctave)
  const targetEvents = chordToEvents(pair.targetChord, '2n', pair.rootOctave)
  const first = pair.targetPosition === 'first' ? targetEvents : referenceEvents
  const second = pair.targetPosition === 'first' ? referenceEvents : targetEvents
  return [
    { events: first, delayMs: 0 },
    { events: second, delayMs: CHORD_MS + GAP_MS },
  ]
}

export interface UseAugDimWarmupPlaybackProps {
  pair: AugDimWarmupPair | null
}

/**
 * Hook returning `{ play, stop }`. A null pair yields an empty stab list
 * (no-op play), so the caller can mount the hook unconditionally regardless
 * of the active sub-mode.
 */
export const useAugDimWarmupPlayback = (
  props: UseAugDimWarmupPlaybackProps
): { play: () => void; stop: () => void } => {
  const { play, stop } = useStabScheduler({
    buildStabs: () => (props.pair ? buildAugDimWarmupStabs(props.pair) : []),
  })
  return { play, stop }
}
